import { type RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { type MenuAlignment } from '@common/components/Menu';
import getScrollbarSize from '@common/utils/ui/getScrollbarSize';

const allEvents = ['resize', 'scroll'];

const getContextBounds = (elem: HTMLElement) => {
  const context = elem.closest('.drawer')
    ?? window.document.querySelector('section.section-content');
  if (!context) {
    return undefined;
  }
  const styles = getComputedStyle(context);
  const { top, bottom, left, right } = context.getBoundingClientRect();
  const scrollbarSize = getScrollbarSize();
  return {
    ctxTop: top + Number.parseFloat(styles.paddingTop),
    ctxBottom: bottom - Number.parseFloat(styles.paddingBottom) - scrollbarSize.height,
    ctxLeft: left + Number.parseFloat(styles.paddingLeft),
    ctxRight: right - Number.parseFloat(styles.paddingRight) - scrollbarSize.width,
  };
};

export type ForceDirection = 'up' | 'down';

const useElementMaxSize = (
  elementRef: RefObject<HTMLElement | null>,
  forceDirection?: ForceDirection,
  align?: MenuAlignment,
) => {
  const [toTop, setToTop] = useState<number>();
  const [toBottom, setToBottom] = useState<number>();
  const [toLeft, setToLeft] = useState<number>();
  const [toRight, setToRight] = useState<number>();

  const allEventsHandler = useCallback(() => {
    if (!elementRef.current) {
      return;
    }
    const { top, bottom, left, right } = elementRef.current.getBoundingClientRect();
    const { ctxTop, ctxBottom, ctxLeft, ctxRight } = getContextBounds(elementRef.current) ?? {};
    if (ctxTop != null && ctxBottom != null && ctxLeft != null && ctxRight != null) {
      setToTop(top - ctxTop);
      setToBottom(Math.min(ctxBottom, window.innerHeight) - bottom);
      setToLeft(left - ctxLeft);
      setToRight(Math.min(ctxRight, window.innerWidth) - right);
    }
  }, [elementRef.current, getContextBounds]);

  useEffect(
    () => {
      allEvents.forEach((type) => window.addEventListener(type, allEventsHandler));
      return () => {
        allEvents.forEach((type) => window.removeEventListener(type, allEventsHandler));
      };
    },
    [allEventsHandler],
  );

  useEffect(() => allEventsHandler(), [allEventsHandler]);

  const openUpwards = useMemo(() => {
    if (forceDirection === 'up') {
      return true;
    }
    if (forceDirection === 'down') {
      return false;
    }
    if (toBottom != null && toTop != null) {
      if (toBottom < 200) {
        return toBottom < toTop;
      }
    }
    return false;
  }, [forceDirection, toBottom, toTop]);

  const maxHeight = useMemo(() => {
    return openUpwards ? toTop : toBottom;
  }, [openUpwards, toBottom, toTop]);

  const maxWidth = useMemo(() => {
    return align === 'left' ? toRight : toLeft;
  }, [align, toLeft, toRight]);

  return {
    maxHeight: maxHeight ? maxHeight - 12 : undefined, // margin of 12px
    maxWidth: maxWidth ? maxWidth - 12 : undefined,
    openUpwards,
  };
};

export default useElementMaxSize;
